import { useCallback, useEffect, useRef, useState } from "react";

import config from "@/config";
import { VOB_READY_PAGE_SIZE } from "@/lib/constants";
import type {
  VobBlockResponse,
  VobGenerateResponse,
  VobMarkReadyResponse,
  VobReadyCaseSummary,
  VobReadyGroup,
  VobReadyGroupsResponse,
  VobReadyGroupStatusResponse,
} from "@/types/vob-ready";

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError";
}

const VOB_POLL_INTERVAL_MS = 5_000;
const VOB_POLL_TIMEOUT_MS = 10 * 60 * 1000;

function groupKey(group: VobReadyGroup): string {
  return `${group.patient_id}|${group.j_code}|${group.date}`;
}

function groupHasInProgressCases(group: VobReadyGroup | null | undefined): boolean {
  return (
    group?.cases.some((c) => c.vob_generation_status === "in_progress") ?? false
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function applyGroupCaseUpdates(
  group: VobReadyGroup,
  caseIds: string[],
  patch: Partial<VobReadyCaseSummary>,
): (prev: VobReadyGroup[]) => VobReadyGroup[] {
  const key = groupKey(group);
  const idSet = new Set(caseIds);

  return (prev) =>
    prev.map((existing) => {
      if (groupKey(existing) !== key) return existing;
      return {
        ...existing,
        cases: existing.cases.map((caseSummary) =>
          idSet.has(caseSummary.id) ? { ...caseSummary, ...patch } : caseSummary,
        ),
      };
    });
}

interface UseVobReadyGroupsOptions {
  month?: string;
  search?: string;
  showMarkedReady?: boolean;
  showBlocked?: boolean;
}

export function useVobReadyGroups({
  month,
  search,
  showMarkedReady = false,
  showBlocked = false,
}: UseVobReadyGroupsOptions = {}) {
  const [groups, setGroups] = useState<VobReadyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [caseCount, setCaseCount] = useState(0);
  const [totalGroupCount, setTotalGroupCount] = useState(0);
  const [filteredGroupCount, setFilteredGroupCount] = useState(0);
  const [markedReadyGroupCount, setMarkedReadyGroupCount] = useState(0);
  const [notMarkedReadyGroupCount, setNotMarkedReadyGroupCount] = useState(0);
  const [generatingKeys, setGeneratingKeys] = useState<Set<string>>(new Set());
  const [markingReadyKeys, setMarkingReadyKeys] = useState<Set<string>>(new Set());
  const [unmarkingReadyKeys, setUnmarkingReadyKeys] = useState<Set<string>>(new Set());
  const [blockingKeys, setBlockingKeys] = useState<Set<string>>(new Set());
  const [generateError, setGenerateError] = useState<string | null>(null);

  const monthRef = useRef(month);
  monthRef.current = month;
  const searchRef = useRef(search);
  searchRef.current = search;
  const showMarkedReadyRef = useRef(showMarkedReady);
  showMarkedReadyRef.current = showMarkedReady;
  const showBlockedRef = useRef(showBlocked);
  showBlockedRef.current = showBlocked;

  const abortRef = useRef<AbortController | null>(null);
  const fetchSeqRef = useRef(0);
  const pollAbortByKeyRef = useRef<Map<string, AbortController>>(new Map());

  const stopPollingForGroup = useCallback((key: string) => {
    pollAbortByKeyRef.current.get(key)?.abort();
    pollAbortByKeyRef.current.delete(key);
  }, []);

  const stopAllPolling = useCallback(() => {
    for (const controller of pollAbortByKeyRef.current.values()) {
      controller.abort();
    }
    pollAbortByKeyRef.current.clear();
  }, []);

  const fetchGroupStatus = useCallback(
    async (group: VobReadyGroup, signal?: AbortSignal): Promise<VobReadyGroup | null> => {
      const params = new URLSearchParams();
      params.set("patient_id", group.patient_id);
      params.set("j_code", group.j_code);
      params.set("date", group.date);

      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/vob-ready-group-status?${params.toString()}`,
        { signal },
      );
      if (!res.ok) return null;
      const data = (await res.json()) as VobReadyGroupStatusResponse;
      return data.group ?? null;
    },
    [],
  );

  const mergeGroupUpdates = useCallback((incoming: VobReadyGroup[]) => {
    setGroups((prev) => mergeGroups(prev, incoming));
  }, []);

  const pollGroupUntilTerminal = useCallback(
    async (group: VobReadyGroup, signal: AbortSignal) => {
      const deadline = Date.now() + VOB_POLL_TIMEOUT_MS;

      const pollOnce = async (): Promise<boolean> => {
        const updated = await fetchGroupStatus(group, signal);
        if (!updated) return false;

        mergeGroupUpdates([updated]);
        return !groupHasInProgressCases(updated);
      };

      if (await pollOnce()) return;

      while (Date.now() < deadline) {
        if (signal.aborted) return;

        await sleep(VOB_POLL_INTERVAL_MS);
        if (signal.aborted) return;

        if (await pollOnce()) return;
      }
    },
    [fetchGroupStatus, mergeGroupUpdates],
  );

  const startBackgroundPoll = useCallback(
    (group: VobReadyGroup) => {
      const key = groupKey(group);
      stopPollingForGroup(key);

      const ac = new AbortController();
      pollAbortByKeyRef.current.set(key, ac);

      void pollGroupUntilTerminal(group, ac.signal).finally(() => {
        if (pollAbortByKeyRef.current.get(key) === ac) {
          pollAbortByKeyRef.current.delete(key);
        }
      });
    },
    [pollGroupUntilTerminal, stopPollingForGroup],
  );

  const fetchPage = useCallback(async (cursor?: string | null, append = false) => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    const seq = ++fetchSeqRef.current;

    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setError(null);
    }

    try {
      const params = new URLSearchParams();
      params.set("limit", String(VOB_READY_PAGE_SIZE));
      if (cursor) params.set("starting_after", cursor);
      if (monthRef.current?.trim()) params.set("month", monthRef.current.trim());
      const searchTerm = searchRef.current?.trim();
      if (searchTerm) params.set("search", searchTerm);
      if (showBlockedRef.current) {
        params.set("blocked", "true");
      } else {
        params.set("marked_ready", showMarkedReadyRef.current ? "true" : "false");
      }

      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/vob-ready-groups?${params.toString()}`,
        { signal: ac.signal },
      );
      if (!res.ok) throw new Error(`VOB-ready fetch failed: ${res.status}`);
      const data = (await res.json()) as VobReadyGroupsResponse;
      if (seq !== fetchSeqRef.current) return;

      setGroups((prev) => (append ? mergeGroups(prev, data.groups) : data.groups));
      setHasMore(Boolean(data.has_more));
      setNextCursor(data.next_cursor);
      setCaseCount(data.case_count);
      setTotalGroupCount(data.total_group_count);
      setFilteredGroupCount(data.filtered_group_count);
      setMarkedReadyGroupCount(data.marked_ready_group_count);
      setNotMarkedReadyGroupCount(data.not_marked_ready_group_count);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      if (isAbortError(err)) return;
      setError(err instanceof Error ? err.message : "Failed to fetch VOB-ready groups");
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchPage(null, false);
    return () => {
      abortRef.current?.abort();
      stopAllPolling();
    };
  }, [fetchPage, month, search, showMarkedReady, showBlocked, stopAllPolling]);

  useEffect(() => {
    for (const group of groups) {
      const key = groupKey(group);
      if (groupHasInProgressCases(group) && !pollAbortByKeyRef.current.has(key)) {
        startBackgroundPoll(group);
      }
    }
  }, [groups, startBackgroundPoll]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore || !nextCursor) return;
    void fetchPage(nextCursor, true);
  }, [fetchPage, hasMore, loadingMore, nextCursor]);

  const refresh = useCallback(() => {
    void fetchPage(null, false);
  }, [fetchPage]);

  const generateVob = useCallback(
    async (group: VobReadyGroup) => {
      const key = groupKey(group);
      setGenerateError(null);
      setGeneratingKeys((prev) => new Set(prev).add(key));
      try {
        const res = await fetch(`${config.API_BASE_URL}/api/dashboard/vob/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            patient_id: group.patient_id,
            j_code: group.j_code,
            date: group.date,
          }),
        });
        const data = (await res.json().catch(() => ({}))) as
          | VobGenerateResponse
          | { detail?: unknown };
        if (!res.ok) {
          const detail =
            typeof data === "object" && data && "detail" in data
              ? String(
                  typeof data.detail === "string"
                    ? data.detail
                    : JSON.stringify(data.detail),
                )
              : `VOB generation failed: ${res.status}`;
          throw new Error(detail);
        }

        const success = data as VobGenerateResponse;
        setGroups(applyGroupCaseUpdates(group, success.case_ids, {
          vob_generation_status: "in_progress",
          vob_marked_ready: false,
          vob_generation_error: null,
          vob_storage_uri: null,
          vob_docx_filename: null,
        }));
        startBackgroundPoll(group);
      } catch (err) {
        if (!isAbortError(err)) {
          setGenerateError(
            err instanceof Error ? err.message : "Failed to start VOB generation",
          );
        }
      } finally {
        setGeneratingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [startBackgroundPoll],
  );

  const markVobReady = useCallback(
    async (group: VobReadyGroup) => {
      const key = groupKey(group);
      setGenerateError(null);
      setMarkingReadyKeys((prev) => new Set(prev).add(key));
      try {
        const res = await fetch(`${config.API_BASE_URL}/api/dashboard/vob/mark-ready`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            patient_id: group.patient_id,
            j_code: group.j_code,
            date: group.date,
          }),
        });
        const data = (await res.json().catch(() => ({}))) as
          | VobMarkReadyResponse
          | { detail?: unknown };
        if (!res.ok) {
          const detail =
            typeof data === "object" && data && "detail" in data
              ? String(
                  typeof data.detail === "string"
                    ? data.detail
                    : JSON.stringify(data.detail),
                )
              : `Mark as ready failed: ${res.status}`;
          throw new Error(detail);
        }

        const success = data as VobMarkReadyResponse;
        if (!showMarkedReadyRef.current) {
          setGroups((prev) => prev.filter((existing) => groupKey(existing) !== key));
          setNotMarkedReadyGroupCount((count) => Math.max(0, count - 1));
          setMarkedReadyGroupCount((count) => count + 1);
          setFilteredGroupCount((count) => Math.max(0, count - 1));
        } else {
          setGroups(applyGroupCaseUpdates(group, success.case_ids, {
            vob_marked_ready: true,
          }));
        }
      } catch (err) {
        if (!isAbortError(err)) {
          setGenerateError(
            err instanceof Error ? err.message : "Failed to mark VOB package as ready",
          );
        }
      } finally {
        setMarkingReadyKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [],
  );

  const unmarkVobReady = useCallback(
    async (group: VobReadyGroup) => {
      const key = groupKey(group);
      setGenerateError(null);
      setUnmarkingReadyKeys((prev) => new Set(prev).add(key));
      try {
        const res = await fetch(`${config.API_BASE_URL}/api/dashboard/vob/unmark-ready`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            patient_id: group.patient_id,
            j_code: group.j_code,
            date: group.date,
          }),
        });
        const data = (await res.json().catch(() => ({}))) as
          | VobMarkReadyResponse
          | { detail?: unknown };
        if (!res.ok) {
          const detail =
            typeof data === "object" && data && "detail" in data
              ? String(
                  typeof data.detail === "string"
                    ? data.detail
                    : JSON.stringify(data.detail),
                )
              : `Unmark ready failed: ${res.status}`;
          throw new Error(detail);
        }

        const success = data as VobMarkReadyResponse;
        if (showMarkedReadyRef.current) {
          setGroups((prev) => prev.filter((existing) => groupKey(existing) !== key));
          setMarkedReadyGroupCount((count) => Math.max(0, count - 1));
          setNotMarkedReadyGroupCount((count) => count + 1);
          setFilteredGroupCount((count) => Math.max(0, count - 1));
        } else {
          setGroups(applyGroupCaseUpdates(group, success.case_ids, {
            vob_marked_ready: false,
          }));
        }
      } catch (err) {
        if (!isAbortError(err)) {
          setGenerateError(
            err instanceof Error ? err.message : "Failed to unmark VOB package as ready",
          );
        }
      } finally {
        setUnmarkingReadyKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [],
  );

  const blockVob = useCallback(
    async (group: VobReadyGroup, blockDetails?: string) => {
      const key = groupKey(group);
      setGenerateError(null);
      setBlockingKeys((prev) => new Set(prev).add(key));
      try {
        const payload: Record<string, string> = {
          patient_id: group.patient_id,
          j_code: group.j_code,
          date: group.date,
        };
        const note = (blockDetails ?? "").trim();
        if (note) {
          payload.block_details = note;
        }

        const res = await fetch(`${config.API_BASE_URL}/api/dashboard/vob/block`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = (await res.json().catch(() => ({}))) as
          | VobBlockResponse
          | { detail?: unknown };
        if (!res.ok) {
          const detail =
            typeof data === "object" && data && "detail" in data
              ? String(
                  typeof data.detail === "string"
                    ? data.detail
                    : JSON.stringify(data.detail),
                )
              : `Block VOB failed: ${res.status}`;
          throw new Error(detail);
        }

        stopPollingForGroup(key);
        setGroups((prev) => prev.filter((existing) => groupKey(existing) !== key));
        await refresh();
      } catch (err) {
        if (!isAbortError(err)) {
          setGenerateError(
            err instanceof Error ? err.message : "Failed to block VOB package",
          );
        }
      } finally {
        setBlockingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [stopPollingForGroup, refresh],
  );

  return {
    groups,
    loading,
    loadingMore,
    error,
    hasMore,
    caseCount,
    totalGroupCount,
    filteredGroupCount,
    markedReadyGroupCount,
    notMarkedReadyGroupCount,
    loadMore,
    refresh,
    generateVob,
    markVobReady,
    unmarkVobReady,
    blockVob,
    generatingKeys,
    markingReadyKeys,
    unmarkingReadyKeys,
    blockingKeys,
    generateError,
  };
}

function mergeGroupCasesInPlace(
  existing: VobReadyGroup,
  incoming: VobReadyGroup,
): VobReadyGroup {
  const incomingCases = new Map(incoming.cases.map((c) => [c.id, c]));
  const seen = new Set<string>();
  const cases = existing.cases.map((c) => {
    const fresh = incomingCases.get(c.id);
    if (fresh) {
      seen.add(c.id);
      return fresh;
    }
    return c;
  });
  for (const caseSummary of incoming.cases) {
    if (!seen.has(caseSummary.id)) {
      cases.push(caseSummary);
    }
  }
  return {
    ...existing,
    patient_name: incoming.patient_name ?? existing.patient_name,
    case_count: cases.length,
    cases,
  };
}

function mergeGroups(existing: VobReadyGroup[], incoming: VobReadyGroup[]): VobReadyGroup[] {
  const incomingByKey = new Map(incoming.map((g) => [groupKey(g), g]));
  const merged = existing.map((group) => {
    const updated = incomingByKey.get(groupKey(group));
    if (!updated) return group;
    incomingByKey.delete(groupKey(group));
    return mergeGroupCasesInPlace(group, updated);
  });
  for (const group of incomingByKey.values()) {
    merged.push({ ...group, cases: [...group.cases] });
  }
  return merged;
}
